// localStorage-backed pointer to live online rooms, keyed by room code. Lets
// a refreshed tab rejoin within the server's grace window without
// re-entering the lobby, and lets one player hold sessions for more than
// one room at a time without one clobbering the other.

// Matches RECONNECT_WINDOW_MS (packages/room-server/src/RoomManager.ts) — the
// server reaps an all-anonymous room 5 minutes after it goes fully empty, so
// a longer local TTL would just offer a "resume" that's already gone. An app
// whose seats can be identity-bound (their reclaim survives as long as the
// room does, up to ROOM_TIMEOUT_MS) should pass a longer graceWindowMs
// explicitly rather than change what every other consumer defaults to.
const DEFAULT_GRACE_WINDOW_MS = 300_000;

/** Matches ROOM_TIMEOUT_MS in packages/room-server/src/RoomManager.ts — the
 * grace window for identity-bound seats (their reclaim survives as long as
 * the room does), for consumers passing a longer graceWindowMs explicitly. */
export const IDENTITY_ROOM_GRACE_WINDOW_MS = 3_600_000;

export interface SessionEntry {
  readonly roomCode: string;
  readonly sessionToken: string;
  readonly expiresAt: number;
}

interface StoredRoomEntry {
  readonly sessionToken: string;
  readonly expiresAt: number;
}

type SessionMap = Record<string, StoredRoomEntry>;

// The pre-T5 shape was a single entry, not a room-keyed map. Detected on
// read so players mid-game across the deploy don't lose their seat.
function isLegacyEntry(value: unknown): value is SessionEntry {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.roomCode === "string" &&
    typeof candidate.sessionToken === "string" &&
    typeof candidate.expiresAt === "number"
  );
}

function isStoredRoomEntry(value: unknown): value is StoredRoomEntry {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return typeof candidate.sessionToken === "string" && typeof candidate.expiresAt === "number";
}

export class SessionStore {
  private readonly storageKey: string;
  private readonly graceWindowMs: number;

  constructor(storageKey: string, graceWindowMs: number = DEFAULT_GRACE_WINDOW_MS) {
    this.storageKey = storageKey;
    this.graceWindowMs = graceWindowMs;
  }

  save(roomCode: string, sessionToken: string): void {
    this.saveFor(roomCode, sessionToken);
  }

  saveFor(roomCode: string, sessionToken: string): void {
    try {
      const map = this.readMap();
      const now = Date.now();
      const next: SessionMap = {};
      for (const [code, entry] of Object.entries(map)) {
        if (entry.expiresAt >= now) next[code] = entry;
      }
      next[roomCode] = { sessionToken, expiresAt: now + this.graceWindowMs };
      localStorage.setItem(this.storageKey, JSON.stringify(next));
    } catch {
      // Storage unavailable — silent failure, reconnect just won't survive refresh.
    }
  }

  load(): SessionEntry | null {
    try {
      const map = this.readMap();
      const now = Date.now();
      let bestCode: string | null = null;
      let best: StoredRoomEntry | null = null;
      let expiredFound = false;
      for (const [code, entry] of Object.entries(map)) {
        if (entry.expiresAt < now) {
          expiredFound = true;
          continue;
        }
        if (best === null || entry.expiresAt > best.expiresAt) {
          bestCode = code;
          best = entry;
        }
      }
      if (expiredFound) this.pruneExpired(map, now);
      if (bestCode === null || best === null) return null;
      return { roomCode: bestCode, sessionToken: best.sessionToken, expiresAt: best.expiresAt };
    } catch {
      return null;
    }
  }

  loadFor(roomCode: string): SessionEntry | null {
    try {
      const map = this.readMap();
      const entry = map[roomCode];
      if (!entry) return null;
      if (entry.expiresAt < Date.now()) {
        this.clearFor(roomCode);
        return null;
      }
      return { roomCode, sessionToken: entry.sessionToken, expiresAt: entry.expiresAt };
    } catch {
      return null;
    }
  }

  clear(): void {
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
      // ignore
    }
  }

  clearFor(roomCode: string): void {
    try {
      const map = this.readMap();
      if (!(roomCode in map)) return;
      delete map[roomCode];
      localStorage.setItem(this.storageKey, JSON.stringify(map));
    } catch {
      // ignore
    }
  }

  // Reads the stored blob and normalizes it into a SessionMap, migrating
  // the pre-T5 single-entry shape in-memory. No write-back on read: every
  // save path already writes the new format, and reads always re-detect
  // the legacy shape defensively, so a persisted migration isn't needed.
  private readMap(): SessionMap {
    const raw = localStorage.getItem(this.storageKey);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (isLegacyEntry(parsed)) {
      return { [parsed.roomCode]: { sessionToken: parsed.sessionToken, expiresAt: parsed.expiresAt } };
    }
    if (typeof parsed !== "object" || parsed === null) return {};
    const map: SessionMap = {};
    for (const [code, entry] of Object.entries(parsed as Record<string, unknown>)) {
      if (isStoredRoomEntry(entry)) map[code] = entry;
    }
    return map;
  }

  private pruneExpired(map: SessionMap, now: number): void {
    try {
      const next: SessionMap = {};
      for (const [code, entry] of Object.entries(map)) {
        if (entry.expiresAt >= now) next[code] = entry;
      }
      localStorage.setItem(this.storageKey, JSON.stringify(next));
    } catch {
      // ignore
    }
  }
}
